import { useState } from 'react'
import { getCustomerColor, getCustomerInitials } from '../utils/customerColors'
import './CustomerAvatar.css'

/**
 * CustomerAvatar — shared avatar component for customers.
 *
 * Render priority:
 *   1. Stored logo (logoBase64 data URI) if present and it loads
 *   2. Favicon fetched from company website
 *   3. Deterministic colored initials
 *
 * Props:
 *   customerId  - string (used for deterministic color)
 *   companyName - string (used for initials)
 *   website     - string (optional, company domain for favicon)
 *   logoBase64  - string (optional, stored logo as a data URI or base64 PNG)
 *   size        - 'sm' | 'md' | 'lg' | 'xl'  (default 'md')
 *   className   - additional CSS class
 */

const SIZES = {
  sm: 28,
  md: 36,
  lg: 44,
  xl: 56,
}

const FONT_SIZES = {
  sm: 10,
  md: 12,
  lg: 14,
  xl: 18,
}

const RADII = {
  sm: 6,
  md: 8,
  lg: 10,
  xl: 12,
}

function cleanDomain(input) {
  if (!input) return ''
  return input.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
}

function getFaviconUrl(domain) {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`
}

// Accept either a full data URI or a bare base64 payload.
function normalizeLogo(input) {
  if (!input || typeof input !== 'string') return ''
  const s = input.trim()
  if (!s) return ''
  if (s.startsWith('data:image')) return s
  // bare base64 — assume PNG
  return `data:image/png;base64,${s}`
}

export default function CustomerAvatar({ customerId, companyName, website, logoBase64, size = 'md', className = '' }) {
  const [logoFailed, setLogoFailed] = useState(false)
  const [faviconFailed, setFaviconFailed] = useState(false)

  const logoSrc = normalizeLogo(logoBase64)
  const domain = cleanDomain(website)
  const showLogo = logoSrc && !logoFailed
  const showFavicon = !showLogo && domain && !faviconFailed

  const color = getCustomerColor(customerId || '')
  const initials = getCustomerInitials(companyName || '')
  const px = SIZES[size] || SIZES.md
  const fontSize = FONT_SIZES[size] || FONT_SIZES.md
  const radius = RADII[size] || RADII.md

  const useImage = showLogo || showFavicon

  return (
    <div
      className={`customer-avatar ${className}`}
      style={{
        width: px,
        height: px,
        minWidth: px,
        borderRadius: radius,
        background: useImage ? '#ffffff' : color,
        border: useImage ? '1px solid #e5e7eb' : 'none',
        fontSize: fontSize,
        overflow: 'hidden',
      }}
    >
      {showLogo ? (
        <img
          src={logoSrc}
          alt=""
          className="customer-avatar-logo"
          style={{
            width: px,
            height: px,
            objectFit: 'cover',
            borderRadius: radius,
          }}
          onError={() => setLogoFailed(true)}
        />
      ) : showFavicon ? (
        <img
          src={getFaviconUrl(domain)}
          alt=""
          className="customer-avatar-favicon"
          style={{
            width: Math.round(px * 0.65),
            height: Math.round(px * 0.65),
          }}
          onError={() => setFaviconFailed(true)}
        />
      ) : (
        <span className="customer-avatar-initials">{initials}</span>
      )}
    </div>
  )
}
